import { useCallback, useEffect, useMemo, useState } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TextField from '@material-ui/core/TextField';
import Alert from '@material-ui/lab/Alert';
import { useWindowWidth } from '@react-hook/window-size';
import useGetTranslatedAttribute from 'hooks/useGetTranslatedAttribute';
import sanitizeHtml from 'sanitize-html';

import GreenButton from '../../common/styled/GreenButton/GreenButton';
import CustomLoader from '../../components/CustomLoader/CustomLoader';
import GoodPracticeDetailsDialog from '../../dialogs/GoodPracticeDetailsDialog/GoodPracticeDetailsDialog';
import { ReduxState } from '../../redux/types';
import Company, { EmptyCompany } from '../../types/Company';
import GoodPractice from '../../types/GoodPractice';
import api from '../../utils/api';
import { useDialog } from '../../utils/dialog';
import snackbar from '../../utils/snackbar';
import { ActionTableCell, FlexContainer, StyledButton, Title } from './styled';

interface Props {
    publicPage?: boolean;
    adminPage?: boolean;
}

const CertificatesPage = (props: Props) => {
    const { publicPage, adminPage } = props;
    const { companyId } = useParams<{ companyId: string }>();

    const userInfo = useSelector((state: ReduxState) => state.user);
    const userId = userInfo.id;
    const isCustomer = userInfo.type === 'customer';
    const isAdmin = userInfo.type === 'admin';
    const isCertifier = userInfo.type === 'certifier';

    const realCompanyId = companyId || userInfo.companyId;

    const { t } = useTranslation();
    const [openDialog, closeDialog] = useDialog();

    const windowWidth = useWindowWidth();
    const titleSuffix = windowWidth < 900 ? '_small' : '';

    const [isLoading, setIsLoading] = useState(true);
    const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

    const [companyData, setCompanyData] = useState<Company>(EmptyCompany);
    const [gps, setGps] = useState<GoodPractice[]>([]);
    const [selectedGpId, selectGpId] = useState(0);

    const [searchQuery, setSearchQuery] = useState('');

    const getTranslatedAttribute = useGetTranslatedAttribute();

    const filteredGps = useMemo(() => {
        if (!searchQuery) return gps;

        return gps.filter(g => {
            const translatedTitle = getTranslatedAttribute(g, 'title');
            const translatedDescription = getTranslatedAttribute(g, 'description');
            const translatedItemCategoryDescription = g.itemCategory ? getTranslatedAttribute(g.itemCategory, 'name') : '';

            return (translatedTitle ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (translatedDescription ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (translatedItemCategoryDescription ?? '').toLowerCase().startsWith(searchQuery.toLowerCase());
        });
    }, [gps, searchQuery, getTranslatedAttribute]);

    const getGps = useCallback(() => {
        api.request(publicPage ? '/gps' : ('/companies/' + realCompanyId + '/gps')).then((res) => {
            setGps(res);
            setIsLoading(false);
        });
    }, [publicPage, realCompanyId]);

    const getData = useCallback(() => {
        if (publicPage) {
            getGps();
            return;
        }

        api.request('/companies/' + realCompanyId).then((companyRes) => {
            setCompanyData(companyRes);
            getGps();
        });
    }, [getGps, publicPage, realCompanyId]);

    const openDetailsDialog = useCallback((certificateId: number) => {
        selectGpId(certificateId);
        setIsDetailsDialogOpen(true);
    }, []);

    const closeDetailsDialog = useCallback(() => {
        setIsDetailsDialogOpen(false);
    }, []);

    const deleteGp = useCallback((gpId: number) => {
        api.request('/companies/' + realCompanyId + '/gps/' + gpId, 'DELETE').then(() => {
            snackbar.success(t('dialogs.good_practice_delete.success'));
            getData();
        });
    }, [getData, realCompanyId, t]);

    const removePublicGp = useCallback((gpId: number) => {
        api.request('/gps/' + gpId, 'DELETE').then(() => {
            snackbar.success(t('dialogs.public_good_practice_delete.success'));
            getData();
        });
    }, [getData, t]);

    const deleteGpDialog = useCallback((gpId: number) => {
        openDialog({
            title: t('dialogs.good_practice_delete.title'),
            message: t('dialogs.good_practice_delete.message'),
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        deleteGp(gpId);
                        closeDialog();
                    }
                }
            ]
        });
    }, [closeDialog, deleteGp, openDialog, t]);

    const removePublicGpDialog = useCallback((gpId: number, cId: any) => {
        openDialog({
            title: t('dialogs.public_good_practice_delete.title'),
            message: t('dialogs.public_good_practice_delete.message' + (cId ? '' : '_without_customer')),
            actions: [
                {
                    text: t('dialogs.generic.cancel'),
                    color: 'secondary',
                    action: closeDialog
                },
                {
                    text: t('dialogs.generic.confirm'),
                    color: 'primary',
                    action: () => {
                        removePublicGp(gpId);
                        closeDialog();
                    }
                }
            ]
        });
    }, [removePublicGp, closeDialog, openDialog, t]);

    useEffect(() => {
        getData();
    }, [getData]);

    if (isLoading) {
        return <CustomLoader />;
    }

    return (
        <>
            <FlexContainer>
                <Title variant='h4'>
                    {(companyId && !publicPage) ? (
                        <Trans i18nKey={'pages.good_practices.title_with_company' + titleSuffix} tOptions={{ company: companyData.name }}>
                            _<small>{companyData.name}</small>_
                        </Trans>
                    ) : t('pages.' + (adminPage ? 'admin_' : '') + 'public_good_practices.title' + titleSuffix)}
                </Title>

                {((!publicPage && !isAdmin && !isCertifier) || adminPage) && (
                    <GreenButton variant='outlined' onClick={() => openDetailsDialog(0)} >
                        {t('pages.good_practices.new_gp')}
                    </GreenButton>
                )}
            </FlexContainer>

            {isCustomer && (
                <Alert style={{ maxWidth: '1200px', margin: '12px auto 0' }} severity='info'>
                    <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('pages.good_practices.public_info_message')) }} />
                </Alert>
            )}

            <TextField
                margin='dense'
                variant='outlined'
                color='primary'
                label={'Cerca buona pratica'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
            />

            {filteredGps.length ? (
                <TableContainer component={Paper} style={{ marginTop: '20px' }} >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('pages.good_practices.table.title')}</TableCell>
                                <TableCell>{t('pages.good_practices.table.description')}</TableCell>
                                <TableCell>Categoria</TableCell>
                                {!publicPage && (
                                    <>
                                        {isAdmin && (
                                            <TableCell>{t('pages.good_practices.table.is_public')}</TableCell>
                                        )}
                                        <TableCell>{t('pages.generic.table.actions')}</TableCell>
                                    </>
                                )}
                                {adminPage && (
                                    <TableCell>{t('pages.generic.table.actions')}</TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredGps.map((gp: any) => {
                                return (
                                    <TableRow key={gp.id}>
                                        <TableCell>{getTranslatedAttribute(gp, 'title')}</TableCell>
                                        <TableCell>{getTranslatedAttribute(gp, 'description')}</TableCell>
                                        <TableCell>{gp.itemCategory ? getTranslatedAttribute(gp.itemCategory, 'name') : ''}</TableCell>

                                        {!publicPage ? (
                                            <>
                                                {isAdmin && (
                                                    <TableCell>{t('pages.good_practices.table.is_public_' + (gp.isPublic ? 'yes' : 'no'))}</TableCell>
                                                )}

                                                <ActionTableCell>
                                                    {(isAdmin || gp.userId === userId) && (
                                                        <>
                                                            <StyledButton variant='outlined' color='primary' onClick={() => openDetailsDialog(gp.id)}>
                                                                {t('pages.generic.table.details')}
                                                            </StyledButton>{' '}
                                                            <StyledButton variant='outlined' color='secondary' onClick={() => deleteGpDialog(gp.id)}>
                                                                {t('pages.generic.table.delete')}
                                                            </StyledButton>
                                                        </>
                                                    )}
                                                </ActionTableCell>
                                            </>
                                        ) : (
                                            <>
                                                {adminPage && (
                                                    <ActionTableCell style={{ textAlign: 'right' }}>
                                                        {!gp.companyId && (
                                                            <StyledButton variant='outlined' color='primary' onClick={() => openDetailsDialog(gp.id)}>
                                                                {t('pages.generic.table.details')}
                                                            </StyledButton>
                                                        )}
                                                        <StyledButton variant='outlined' color='secondary' onClick={() => removePublicGpDialog(gp.id, gp.companyId)}>
                                                            {t('pages.generic.table.delete')}
                                                        </StyledButton>
                                                    </ActionTableCell>
                                                )}
                                            </>
                                        )}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Alert style={{ maxWidth: '1200px', margin: '12px auto 0' }} severity='error' >
                    <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('pages.good_practices.no_gps_found')) }} />
                </Alert>
            )}

            <GoodPracticeDetailsDialog
                open={isDetailsDialogOpen}
                onClose={closeDetailsDialog}
                companyId={realCompanyId}
                gpsId={selectedGpId}
                updateData={getGps}
                adminPage={adminPage}
            />
        </>
    );
};

export default CertificatesPage;
